import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["activeStepIndex", "alignLabels", "mapStepsToProps", "labelAppearance", "onStepClick", "orientation", "renderProgressStepperStep", "steps"],
  _excluded2 = ["interactive", "label", "onClick"];
import PropTypes from 'prop-types';
import React, { forwardRef, useCallback, useMemo } from 'react';
import { ORIENTATIONS, PROGRESS_METER_SIZES, PROGRESS_STEPPER_APPEARANCES, PROGRESS_STEPPER_LABEL_ALIGNMENTS, PROGRESS_STEPPER_LABEL_APPEARANCES } from '../../constants';
import CustomPropTypes from '../../util/custom-prop-types';
import { parseSteps } from '../../util/parseSteps';
import { ProgressBar } from '../ProgressBar/ProgressBar';
import { ProgressStepperCurrentLabel } from '../ProgressStepperCurrentLabel/ProgressStepperCurrentLabel';
import { ProgressStepperStep } from '../ProgressStepperStep/ProgressStepperStep';
import { StyledProgressStepper } from './ProgressStepper.styles';

/**
 * @related ['ProgressBar']
 * @subcomponents ['ProgressStepperStep', 'ProgressStepperStepLabel']
 * @since 8.42.0
 */
export var ProgressStepper = /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var activeStepIndex = _ref.activeStepIndex,
    alignLabels = _ref.alignLabels,
    mapStepsToProps = _ref.mapStepsToProps,
    labelAppearance = _ref.labelAppearance,
    onStepClick = _ref.onStepClick,
    orientation = _ref.orientation,
    renderProgressStepperStep = _ref.renderProgressStepperStep,
    stepsProp = _ref.steps,
    rest = _objectWithoutProperties(_ref, _excluded);
  var steps = useMemo(function () {
    return parseSteps(stepsProp, mapStepsToProps);
  }, [mapStepsToProps, stepsProp]);
  var currentLabel = '';
  var currentMeta = '';
  if (activeStepIndex >= 0 && activeStepIndex < steps.length) {
    currentLabel = steps[activeStepIndex].label;
    currentMeta = steps[activeStepIndex].meta;
  }
  var onClickCallBack = useCallback(function (e, index, _ref2) {
    var interactive = _ref2.interactive,
      onClick = _ref2.onClick;
    if (interactive && typeof onStepClick === 'function') {
      onStepClick(e, index);
    }
    if (typeof onClick === 'function') {
      onClick(e);
    }
  }, [onStepClick]);
  var renderStepCallback = useCallback(function (step, index) {
    var interactive = step.interactive,
      label = step.label,
      onClick = step.onClick,
      stepRest = _objectWithoutProperties(step, _excluded2);
    return renderProgressStepperStep(Object.assign({
      active: index === activeStepIndex,
      alignLabel: alignLabels,
      interactive: interactive,
      key: index + label,
      label: label,
      onClick: function onClick(e) {
        return onClickCallBack(e, index, step);
      },
      orientation: orientation
    }, stepRest));
  }, [activeStepIndex, alignLabels, onClickCallBack, orientation, renderProgressStepperStep]);
  return /*#__PURE__*/React.createElement(StyledProgressStepper, _extends({
    activeStepIndex: activeStepIndex,
    alignLabels: alignLabels,
    length: steps.length,
    labelAppearance: labelAppearance,
    orientation: orientation,
    ref: forwardedRef
  }, rest), /*#__PURE__*/React.createElement(ProgressBar, {
    "aria-label": "Step " + (Number(activeStepIndex) + 1) + " of " + steps.length,
    "aria-hidden": "true",
    minValue: 0,
    maxValue: steps.length - 1,
    orientation: orientation,
    size: PROGRESS_METER_SIZES.sm,
    value: activeStepIndex > 0 ? activeStepIndex : 0
  }), steps.map(renderStepCallback), /*#__PURE__*/React.createElement(ProgressStepperCurrentLabel, {
    label: currentLabel,
    meta: currentMeta
  }));
});
ProgressStepper.displayName = 'ProgressStepper';
ProgressStepper.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Zero-based index of the step the user is currently on. This step will
   * display in the "active" state.
   *
   * If you don't want any steps to display as "active", you can use a value either
   * less than `0` or higher than the final step's index. Ex: If the user hasn't started
   * any steps yet, set `activeStepIndex={-1}` and the bar color will indicate no
   * progress. If the user has completed all of five steps, set `activeStepIndex={5}`
   * or higher.
   */
  'activeStepIndex': PropTypes.number,
  /**
   * Sets the horizontal alignment for all step labels.
   *
   * By default, a horizontal ProgressStepper center-aligns the labels. However, when
   * using labels that span multiple lines (ex: a label with a status tag underneath),
   * left-aligning the labels may make them more readable.
   *
   * Note that this only affects a ProgressStepper with horizontal orientation. A
   * vertical ProgressStepper will always have left-aligned labels.
   *
   * Supports inline media query objects.
   *
   * @since 8.57.0
   */
  'alignLabels': CustomPropTypes.inlineMediaQuery(PropTypes.oneOf(Object.values(PROGRESS_STEPPER_LABEL_ALIGNMENTS))),
  /**
   * A unique label that describes this component's purpose.
   * Especially necessary if you have multiple ProgressSteppers on the same page.
   *
   * @since 8.57.0
   */
  'aria-label': PropTypes.string,
  /**
   * Option to show all step labels, only the current step label, or hide all step labels
   * on different breakpoints. Values can be one of "all", "current", or "none".
   *
   * Supports inline media query objects.
   */
  'labelAppearance': CustomPropTypes.inlineMediaQuery(PropTypes.oneOf(Object.values(PROGRESS_STEPPER_LABEL_APPEARANCES))),
  /**
   * A function that maps the values passed to `steps` to an object of props that will be
   * passed to `renderProgressStepperStep`.
   *
   * By default, `mapStepsToProps` will convert strings into an object where the string is
   * a `label`.
   */
  'mapStepsToProps': PropTypes.func,
  /**
   * Called when an interactive step is clicked.
   *
   * This handler will emit the click event as the first
   * argument and the step index as the second.
   */
  'onStepClick': PropTypes.func,
  /**
   * Displays either the horizontal or vertical version of the ProgressStepper.
   *
   * Supports inline media query objects. Ex: You can display
   * the horizontal version on desktop and tablet and the vertical version on phone.
   *
   * @since 8.57.0
   */
  'orientation': CustomPropTypes.inlineMediaQuery(PropTypes.oneOf(Object.values(ORIENTATIONS))),
  /**
   * A function that receives `props` as the only argument and returns a step component, by
   * default a `ProgressStepperStep`.
   */
  'renderProgressStepperStep': PropTypes.func,
  /**
   * ARIA roles provide semantic meaning to content, allowing screen readers and other tools to
   * present and support interaction with object in a way that is consistent with user
   * expectations of that type of object.
   *
   * In this component we feel that `group` is the correct choice
   *
   * @since 8.79.5
   */
  'role': PropTypes.string,
  /**
   * An array of step information.
   *
   * Providing an array of strings will create a simple Progress Stepper with no icons
   * nor interactivity.
   *
   * For a more complex Progress Stepper, you can provide an array of objects that will
   * be passed as props to `ProgressStepperStep`.
   * Objects must contain at least a `label` property (or, if you're using a custom
   * `mapStepsToProps` callback, something equivalent). You can also use the optional `meta`
   * property to show additional content underneath a step label (ex: a `Tag` or `Text`)
   */
  // eslint-disable-next-line zillow/react/forbid-prop-types
  'steps': PropTypes.array.isRequired
} : {};
ProgressStepper.defaultProps = {
  'activeStepIndex': 0,
  'alignLabels': PROGRESS_STEPPER_LABEL_ALIGNMENTS.CENTER,
  'aria-label': 'progress',
  'labelAppearance': {
    default: PROGRESS_STEPPER_LABEL_APPEARANCES.ALL,
    sm_lte: PROGRESS_STEPPER_LABEL_APPEARANCES.CURRENT
  },
  'orientation': ORIENTATIONS.horizontal,
  'renderProgressStepperStep': function renderProgressStepperStep(props) {
    return /*#__PURE__*/React.createElement(ProgressStepperStep, props);
  },
  'role': 'group'
};
ProgressStepper.APPEARANCES = PROGRESS_STEPPER_APPEARANCES;
ProgressStepper.LABEL_ALIGNMENTS = PROGRESS_STEPPER_LABEL_ALIGNMENTS;
ProgressStepper.LABEL_APPEARANCES = PROGRESS_STEPPER_LABEL_APPEARANCES;